/**
 * Pure DB-row → store mappers for profile data.
 *
 * Kept as standalone pure functions (not inlined in auth.tsx) so the
 * field-by-field mapping is unit-testable. The data-room connection fields in
 * particular were historically dropped here — a column existed and a store field
 * existed, but the load-mapping silently omitted them, so the app always read as
 * "not connected" after a fresh login. The test suite locks the full mapping.
 */

import type { Database } from './database.types';
import type { StartupProfile, InvestorProfile } from '@/lib/state/profile-store';
import type { OnboardingStep } from '@/lib/onboarding/steps';

type StartupProfileRow = Database['public']['Tables']['startup_profiles']['Row'];
type InvestorProfileRow = Database['public']['Tables']['investor_profiles']['Row'];

/**
 * Map an investor_profiles row to the store's InvestorProfile shape.
 *
 * Exists for the same reason `mapStartupProfileRow` does, and fixes the same
 * class of bug on the other persona. The load-mapping used to be inline in
 * `auth.tsx` and dropped nearly everything that was not a scalar: the whole
 * thesis, all four social links, and — most visibly — it hardcoded
 * `expertiseAreas: []`, so an investor's expertise tags disappeared on every
 * sign-in even once they had a column to live in.
 *
 * A saved field that is not mapped here is indistinguishable, to the user,
 * from a field that never saved. Both halves have to exist.
 */
export function mapInvestorProfileRow(row: InvestorProfileRow): InvestorProfile {
  const previousRoles = ((row.previous_roles as unknown as {
    title: string;
    company: string;
    years?: string;
  }[]) || []).map((r) => ({ title: r.title, company: r.company, years: r.years }));

  const hasBio = Boolean(row.bio || row.headline || previousRoles.length);
  const hasValueAdd = Boolean(
    row.value_add || row.expertise_areas?.length || row.offers_office_hours || row.board_seats
  );
  const hasThesis = Boolean(
    row.thesis_statement ||
      row.sweet_spot ||
      row.team_preferences?.length ||
      row.deal_breakers?.length ||
      row.investment_focus?.length
  );
  const hasSocial = Boolean(
    row.social_twitter || row.social_website || row.social_calendly || row.social_substack
  );

  return {
    id: row.id,
    firm: row.firm || undefined,
    investorType: (row.investor_type as InvestorProfile['investorType']) || undefined,
    investmentFocus: row.investment_focus || [],
    stagePreference: row.stage_preference || [],
    geographies: row.geographies || undefined,
    ticketSize: { min: row.ticket_size_min || 0, max: row.ticket_size_max || 0 },
    ticketCurrency: row.ticket_currency || undefined,
    leadPreference: (row.lead_preference as InvestorProfile['leadPreference']) || undefined,
    location: row.location || '',
    portfolioCount: row.portfolio_count || 0,
    linkedInVerified: row.linkedin_verified || false,
    linkedInUrl: row.linkedin_url || undefined,
    linkedInVerifiedAt: row.linkedin_verified_at || undefined,
    seniorityLevel: (row.seniority_level as InvestorProfile['seniorityLevel']) || undefined,
    yearsInvesting: row.years_investing || undefined,

    bio: hasBio
      ? {
          headline: row.headline || undefined,
          bio: row.bio || undefined,
          previousRoles: previousRoles.length ? previousRoles : undefined,
        }
      : undefined,

    // `sectors` deliberately reads `investment_focus` — they were the same
    // concept stored twice, and the column is the single home.
    thesis: hasThesis
      ? {
          thesisStatement: row.thesis_statement || undefined,
          sweetSpot: row.sweet_spot || undefined,
          sectors: row.investment_focus || [],
          teamPreferences: row.team_preferences || [],
          dealBreakers: row.deal_breakers || [],
        }
      : undefined,

    valueAdd: hasValueAdd
      ? {
          // Was hardcoded `[]` in the inline mapper.
          expertiseAreas: row.expertise_areas || [],
          description: row.value_add || undefined,
          officeHours: row.offers_office_hours || undefined,
          boardExperience: row.board_seats ?? undefined,
        }
      : undefined,

    socialLinks: hasSocial
      ? {
          twitter: row.social_twitter || undefined,
          website: row.social_website || undefined,
          calendly: row.social_calendly || undefined,
          substack: row.social_substack || undefined,
        }
      : undefined,
  };
}

/**
 * Map a startup_profiles row to the store's StartupProfile shape.
 */
export function mapStartupProfileRow(row: StartupProfileRow): StartupProfile {
  const provider = (row.data_room_provider as StartupProfile['dataRoomProvider'] | null) ?? undefined;
  const folderId = row.data_room_folder_id ?? undefined;

  return {
    id: row.id,
    name: row.name || '',
    industry: row.industry || '',
    // No 'pre-seed' fallback here — an unset stage must read as unset, not a
    // silent default (a pre-seed/$0 essentials value polluted score inputs
    // before this step existed). Cast bypasses StartupProfile['stage']'s
    // non-null type.
    stage: ((row.stage as StartupProfile['stage']) || null) as StartupProfile['stage'],
    location: row.location || '',
    website: row.website || '',
    // `description` is a store-only field with NO startup_profiles column (see
    // buildStartupProfileUpdate note) — it must not be confused with `about`.
    description: '',
    // `tagline` + `about` are real columns (about: migration 20260708215437).
    tagline: row.tagline || '',
    about: row.about || '',
    foundedYear: row.founded_year || undefined,
    teamSize: row.team_size || undefined,
    dataRoomConnected: row.data_room_connected ?? false,
    dataRoomProvider: provider,
    dataRoomFolderId: folderId,
  } as unknown as StartupProfile;
}

/** The store StartupProfile fields the company-profile edit form persists. */
export type EditableStartupProfileFields = Pick<
  StartupProfile,
  | 'name'
  | 'tagline'
  | 'industry'
  | 'stage'
  | 'location'
  | 'foundedYear'
  | 'teamSize'
  | 'website'
  | 'linkedIn'
  | 'fundingTarget'
  | 'problem'
  | 'solution'
  | 'targetCustomer'
  | 'monthlyRevenue'
  | 'userCount'
  | 'growthRate'
  | 'marketSize'
>;

/**
 * Map the store's (camelCase) editable StartupProfile fields to a
 * `startup_profiles` UPDATE payload (snake_case columns).
 *
 * Reverse direction of `mapStartupProfileRow`, used by the company-profile
 * edit screen's real persistence (replaces the former "Simulate API delay"
 * mock save, which updated the store but never wrote the DB).
 *
 * `description` and `minimumTicket` are store-only fields with no DB column
 * and are intentionally omitted. `description` in particular must NOT be
 * confused with the `about` column: `about` is a narrative published only via
 * the scoring-interview digest's allowlisted publish path (see
 * `20260708215437_add_startup_about.sql`), not general profile edits.
 */
export function buildStartupProfileUpdate(
  fields: EditableStartupProfileFields
): Database['public']['Tables']['startup_profiles']['Update'] {
  return {
    name: fields.name,
    tagline: fields.tagline || null,
    industry: fields.industry,
    stage: fields.stage,
    location: fields.location || null,
    founded_year: fields.foundedYear ?? null,
    team_size: fields.teamSize ?? null,
    website: fields.website ?? null,
    linkedin: fields.linkedIn ?? null,
    funding_target: fields.fundingTarget ?? null,
    problem: fields.problem ?? null,
    solution: fields.solution ?? null,
    target_customer: fields.targetCustomer ?? null,
    monthly_revenue: fields.monthlyRevenue ?? null,
    user_count: fields.userCount ?? null,
    growth_rate: fields.growthRate ?? null,
    market_size: fields.marketSize ?? null,
  };
}

/** 3-state fundraising intent (Task 1 migration `startup_profiles.raising_status`). */
export type RaisingStatus = 'raising' | 'not_raising' | 'not_yet';

/**
 * Build the `startup_profiles` UPDATE payload for the onboarding "essentials"
 * step (stage + fundraising + founded year + team size). Unlike
 * `buildStartupProfileUpdate` (the full company-profile edit form, which always
 * writes every field), this mapper OMITS unset inputs entirely — a value the
 * user never provided must never be written as a silent default (`pre-seed`,
 * `$0`, current year, team-of-1 all polluted scoring before this step existed).
 * Explicit `null` is treated the same as "unset", not zero.
 *
 * `raisingStatus` is the canonical 3-state intent; `is_actively_raising` (the
 * legacy boolean the rest of the app still reads) is kept in sync from it —
 * only `'raising'` counts as actively raising.
 */
export function buildEssentialsUpdate(input: {
  stage?: string | null;
  raisingStatus?: RaisingStatus | null;
  fundingTarget?: number | null;
  foundedYear?: number | null;
  teamSize?: number | null;
}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (input.stage != null) out.stage = input.stage;
  if (input.raisingStatus != null) {
    out.raising_status = input.raisingStatus;
    out.is_actively_raising = input.raisingStatus === 'raising';
  }
  if (input.fundingTarget != null) out.funding_target = input.fundingTarget;
  if (input.foundedYear != null) out.founded_year = input.foundedYear;
  if (input.teamSize != null) out.team_size = input.teamSize;
  return out;
}

/**
 * Build the `profiles` UPDATE payload for the onboarding resume pointer
 * (`onboarding_step`, read back by `resolveOnboardingRoute` /
 * `resolveResumeStep` in `@/lib/onboarding/steps` on next login/reload).
 *
 * On completion the pointer is cleared to `null` rather than left holding the
 * last step id — a lingering non-null step would make `resolveResumeStep`
 * treat a completed user as still mid-flow.
 */
export function buildProfileOnboardingUpdate(
  step: OnboardingStep | null,
  completed: boolean,
): { onboarding_step: string | null; onboarding_completed?: boolean } {
  if (completed) return { onboarding_step: null, onboarding_completed: true };
  return { onboarding_step: step };
}
